import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  AppInput,
  AppCard,
  AppRemixIcon,
  AppDatePicker,
  AppMultiSelect,
  AppNoInfo,
} from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import AdvancedSearchBase from './AdvancedSearchBase';
import AdvancedSearchSidebar from '../../../layout/screenSpecifics/Modules/AdvancedSearch/Sidebar/AdvancedSearchSidebar';
import { Formik } from 'formik';
import axios from 'axios';
import { axiosPath } from '@/routing/helpers/router';
import colors from '../../../constants/colors';
import AdvancedSearchResultSwitch from '@/layout/screenSpecifics/Modules/AdvancedSearch/AdvancedSearchResultSwitch';
import * as Yup from 'yup';
import moment from 'moment';

export default function AdvancedSearchHomeScreen() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const [searchResults, setSearchResults] = useState();
  const [searchResultsType, setSearchResultsType] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [validationSchema] = useState(
    Yup.object().shape({
      type: Yup.array().min(1, 'Debe seleccionar al menos un origen'),
      dateStart: Yup.string().required('Debe seleccionar una fecha desde'),
      dateEnd: Yup.string()
        .required('Debe seleccionar una fecha hasta')
    }),
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const handleSearch = useCallback(async (values) => {
    setSearchResults(undefined);
    setSearchResultsType(undefined);
    setIsLoading(true);
    await axios
      .post(axiosPath('api_r_user_module_advancedSearch_search'), {
        moduleBridgeHex,
        filters: values,
      })
      .then(({ data }) => {
        console.log(data);
        setSearchResults(data.results);
        setSearchResultsType(data.type);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <>
      <AdvancedSearchBase />
      <AdvancedSearchSidebar activeRoute="user_module_advancedSearch_home" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [{ name: moduleData?.moduleName }, { name: 'Inicio' }]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <Formik
            initialValues={{
              dateStart: '',
              dateEnd: '',
              type: [],
            }}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={(v) => handleSearch(v)}
          >
            {({ setFieldValue, handleSubmit, values, isSubmitting, errors }) => (
              <form onSubmit={handleSubmit}>
                <section className="content mb-3">
                  <div className="row">
                    <div className="col-12">
                      <AppCard
                        header="Buscar"
                        footer={
                          <div className="row">
                            <div className="col-12">
                              <button
                                type="submit"
                                className="btn btn-primary float-right"
                                disabled={isSubmitting}
                              >
                                Buscar
                              </button>
                            </div>
                          </div>
                        }
                      >
                        <div className="row">
                          <div className="col-md-6">
                            <AppMultiSelect
                              options={[
                                { label: 'BOE - España', value: 'BOE' },
                                { label: '-----', value: null, disabled: true },
                                { label: 'BOA - Aragón', value: 'BOA' },
                                { label: 'BOIB - Baleares', value: 'BOIB' },
                                { label: 'BOPV - País Vasco', value: 'BOPV' },
                                { label: 'BOC - Canarias', value: 'BOC' },
                                { label: 'BOCT - Cantabria', value: 'BOCT' },
                                { label: 'DOCM - Castilla la Mancha', value: 'DOCM' },
                                { label: 'BOCYL - Castilla y León', value: 'BOCYL' },
                                { label: 'DOGC - Cataluña', value: 'DOGC' },
                                { label: 'DOG - Galicia', value: 'DOG' },
                                { label: 'DOE - Extremadura', value: 'DOE' },
                                { label: 'BOJA - Andalucía', value: 'BOJA' },
                                { label: 'BOPA - Asturias', value: 'BOPA' },
                                { label: 'BOR - La Rioja', value: 'BOR' },
                                { label: 'BOCM - Madrid', value: 'BOCM' },
                                { label: 'BORM - Murcia', value: 'BORM' },
                                { label: 'BON - Navarra', value: 'BON' },
                                { label: 'DOGV - Valencia', value: 'DOGV' },
                              ]}
                              title="Tipo"
                              icon={<AppRemixIcon icon="font-color" noVariants />}
                              onChange={(v) => setFieldValue('type', v)}
                              value={values.type}
                              error={errors.type}
                              hasSearchBox
                              hasSelectOptions
                            />
                          </div>
                          <div className="col-lg-3">
                            <AppDatePicker
                              title="Desde"
                              value={values.dateStart}
                              onChange={(date) => setFieldValue('dateStart', date)}
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                              dropdownMode="select"
                              popperClassName="react-datepicker-popper-100"
                              error={errors.dateStart}
                            />
                          </div>
                          <div className="col-lg-3">
                            <AppDatePicker
                              title="Hasta"
                              value={values.dateEnd}
                              onChange={(date) => setFieldValue('dateEnd', date)}
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                              dropdownMode="select"
                              popperClassName="react-datepicker-popper-100"
                              error={errors.dateEnd}
                            />
                          </div>
                        </div>
                      </AppCard>
                    </div>
                  </div>
                </section>
              </form>
            )}
          </Formik>
          <AppCard header={`Resultados (${searchResults?.length})`}>
            {isLoading ? (
              <AppNoInfo />
            ) : searchResults === undefined ? (
              <AppNoInfo
                text="Seleccionar filtros para buscar"
                icon={<AppRemixIcon icon="search" color={colors.grey500} size={34} />}
              />
            ) : searchResults.length > 0 ? (
              <AdvancedSearchResultSwitch
                type={searchResultsType}
                data={searchResults}
              />
            ) : (
              <AppNoInfo
                text="No se encontraron resultados para los filtros seleccionados"
                icon={<AppRemixIcon icon="search" color={colors.grey500} size={34} />}
              />
            )}
          </AppCard>
        </div>
      </main>
    </>
  );
}
